import { Component, createEffect, createMemo, createSignal, onMount, Show } from 'solid-js'
import { Plus, Save, X, Import, Download, SlidersVertical, Dices, Image } from 'lucide-solid'
import Button from '../../shared/Button'
import PageHeader from '../../shared/PageHeader'
import TextInput, { ButtonInput } from '../../shared/TextInput'
import { FormLabel } from '../../shared/FormLabel'
import FileInput, { FileInputResult } from '../../shared/FileInput'
import {
  characterStore,
  tagStore,
  toastStore,
  chatStore,
  userStore,
  settingStore,
} from '../../store'
import { useNavigate, useSearchParams } from '@solidjs/router'
import PersonaAttributes from '../../shared/PersonaAttributes'
import Select from '../../shared/Select'
import TagInput from '../../shared/TagInput'
import { CultureCodes } from '../../shared/CultureCodes'
import VoicePicker from './components/VoicePicker'
import { AppSchema } from '../../../common/types/schema'
import { JSX } from 'solid-js'
import { Card, SolidCard, TitleCard } from '../../shared/Card'
import { usePane } from '../../shared/hooks'
import { RootModal } from '/web/shared/Modal'
import { Toggle } from '../../shared/Toggle'
import { useCharEditor } from './editor'
import { downloadCharacterHub, jsonToCharacter } from './port'
import { DownloadModal } from './DownloadModal'
import ImportCharacterModal from './ImportCharacter'
import Tabs, { useTabs } from '/web/shared/Tabs'
import { getAssetUrl, random } from '/web/shared/util'
import { imageApi } from '/web/store/data/image'
import { Page } from '/web/Layout'
import { ModeGenSettings } from '/web/shared/Mode/ModeGenSettings'
import { canStartTour, startTour } from '/web/tours'
import { Regenerate } from './form/Regenerate'
import { AvatarModal } from './form/AvatarModal'
import { AlternateGreetingsInput } from './form/AltGreetings'
import { SpriteModal } from './form/SpriteModal'
import { AdvancedOptions } from './form/AdvancedOptions'
import { AvatarField } from './form/AvatarField'
import { UploadFile, fileUploader } from '@solid-primitives/upload'

import {
  createModelGalleryImage,
  getModelGallery,
  removeModelGalleryImage,
} from '/web/store/data/gallery'
import { ModelGalleryImage } from '/common/types/aifans-schema'
import { AIFansImageSettings } from '../Settings/Image/AIFansImageSettings'

const formatOptions = [
  { value: 'attributes', label: 'Attributes (Key: value)' },
  { value: 'text', label: 'Plain Text' },
]

const categoryOptions = [
  { value: 'popular', label: 'Popular' },
  { value: 'creator', label: 'Creator Spotlight' },
  { value: 'trending', label: 'Trending Now' },
  { value: 'online', label: 'Online Creators' },
  { value: 'new', label: 'New' },
  { value: 'hentai', label: 'Hentai Creators' },
]

// Content genre — drives the catalog filter pills on the Characters/Following pages.
const genreOptions = [
  { value: '', label: '— None —' },
  { value: 'Anime', label: 'Anime' },
  { value: 'Superhero', label: 'Superhero' },
  { value: 'Comic', label: 'Comic' },
  { value: 'Fantasy', label: 'Fantasy' },
  { value: 'Hentai', label: 'Hentai' },
]

const backupFormats: any = {
  sbf: { value: 'sbf', label: 'SBF' },
  wpp: { value: 'wpp', label: 'W++' },
  boostyle: { value: 'boostyle', label: 'Boostyle' },
}

export const CreateCharacterForm: Component<{
  chat?: AppSchema.Chat
  editId?: string
  duplicateId?: string
  import?: string
  children?: JSX.Element
  temp?: boolean
  noTitle?: boolean
  footer?: (children: JSX.Element) => void
  close?: () => void
  onSuccess?: (char: AppSchema.Character) => void
}> = (props) => {
  let spriteRef: any

  const [search, setSearch] = useSearchParams()
  const nav = useNavigate()
  const user = userStore()

  const isPage = props.close === undefined

  const paneOrPopup = usePane()
  const cancel = () => {
    if (isPage) {
      nav('/character/list')
    } else {
      props.close?.()
    }
  }
  const query = { import: props.import }
  const [forceNew, setForceNew] = createSignal<boolean>(false)

  const srcId = createMemo(() => props.editId || props.duplicateId || '')
  const [image, setImage] = createSignal<string | undefined>()
  const [files, setFiles] = createSignal<UploadFile[]>([])
  const [controlNetImage, setControlNetImage] = createSignal<string | undefined>()
  const [modelGalleryImagesRemaining, setModelGalleryImagesRemaining] = createSignal(0)
  const [modelGalleryImages, setModelGalleryImages] = createSignal<ModelGalleryImage[]>([])
  const [openPreset, setOpenPreset] = createSignal(false)
  const [presetFooter, setPresetFooter] = createSignal<JSX.Element>()

  const editor = useCharEditor()

  const tagState = tagStore()
  const state = characterStore((s) => {
    const edit = s.editing

    return {
      creating: s.creating,
      edit: forceNew() ? undefined : edit,
      list: s.characters.list,
      loaded: s.characters.loaded,
    }
  })

  const [imgUrl, setImageUrl] = createSignal<string>()

  const [tokens, setTokens] = createSignal({
    name: 0,
    scenario: 0,
    greeting: 0,
    persona: 0,
    sample: 0,
  })

  const [showBuilder, setShowBuilder] = createSignal(false)
  const [converted, setConverted] = createSignal<AppSchema.Character>()
  const [showImport, setImport] = createSignal(false)

  const clearEditor = () => {
    setForceNew(true)
    editor.clear()
  }

  const personaFormats = createMemo(() => {
    const options = formatOptions.slice()
    if (editor.state.personaKind in backupFormats) {
      options.push(backupFormats[editor.state.personaKind])
    }
    return options
  })

  const totalTokens = createMemo(() => {
    const t = tokens()
    return t.name + t.persona + t.sample + t.scenario
  })

  const totalPermanentTokens = createMemo(() => {
    const t = tokens()
    return t.name + t.persona + t.scenario
  })

  const [vipFeaturedImage, setVipFeaturedImage] = createSignal<string | undefined>()

  onMount(async () => {
    characterStore.clearGeneratedAvatar()
    characterStore.clearCharacter()

    if (canStartTour('char')) {
      settingStore.closeMenu()
    }

    startTour('char')

    if (srcId()) {
      characterStore.getCharacter(srcId(), { chat: props.chat })
      loadModelGallery()
    }

    /* Character importing from CharacterHub */
    if (!query.import) return
    try {
      const { file, json } = await downloadCharacterHub(query.import)
      const imageData = await imageApi.getImageData(file)
      const char = jsonToCharacter(json)
      await editor.load(char)
      editor.update({
        book: json.characterBook,
        alternateGreetings: json.alternateGreetings || [],
        avatar: file,
        personaKind: 'text',
      })
      editor.receiveAvatar(file)

      setImage(imageData)
      toastStore.success(`Successfully downloaded from Character Hub`)
    } catch (ex: any) {
      toastStore.error(`Character Hub download failed: ${ex.message}`)
    }
  })

  createEffect(async () => {
    // We know we're waiting for a character to edit, so let's just wait
    if (!state.edit && srcId()) return

    // If this is our first pass: load something no matter what
    if (!editor.original()) {
      if (!srcId()) {
        await editor.loadCached()
        return
      }

      // We have a `srcId`, we need to wait to receive the character we're editing
      if (!state.edit) return

      editor.update('editId', srcId())
      await editor.load(state.edit)
      setImage(state.edit?.avatar)
      return
    }

    // This is a subsequent pass - we already have state
    // We want to avoid unnecessarily clearing/reseting state due to a websocket reconnect

    if (!state.edit) return
    const diffCharLoaded = editor.state.editId !== state.edit._id && state.edit._id === srcId()
    if (diffCharLoaded) {
      editor.update('editId', srcId())
      await editor.load(state.edit)
      setImage(state.edit?.avatar)
      return
    }

    if ('__type' in state.edit) return
    const orig = editor.original()
    if (orig?.__type === 'list_character' && state.edit._id === editor.state.editId) {
      await editor.load(state.edit)
      setImage(state.edit?.avatar)

      if (state.edit?.controlNetImage) {
        const base64 = await imageApi.getImageData(state.edit?.controlNetImage)
        setControlNetImage(base64)
      }

      if (state.edit?.vipFeaturedImage) {
        const base64 = await imageApi.getImageData(state.edit?.vipFeaturedImage)
        setVipFeaturedImage(base64)
      }

      return
    }
  })

  createEffect(() => {
    tagStore.updateTags(state.list)
    props.footer?.(footer)
  })

  const updateFile = async (files: FileInputResult[]) => {
    if (!files.length) {
      editor.update('avatar', undefined)
      setImage(state.edit?.avatar)
      return
    }

    const file = files[0].file
    const data = await editor.receiveAvatar(file)
    setImage(data)
  }

  const updateControlNetImage = async (files: FileInputResult[]) => {
    if (!files.length) {
      editor.update('controlNetImage', undefined)
      setControlNetImage(state.edit?.controlNetImage)
      return
    }

    const file = files[0].file

    if (file) {
      const base64 = await imageApi.getImageData(file)
      editor.update('controlNetImage', base64)
      setControlNetImage(base64)
    }
  }

  const updateVipFeaturedImage = async (files: FileInputResult[]) => {
    if (!files.length) {
      editor.update('vipFeaturedImage', undefined)
      setVipFeaturedImage(state.edit?.vipFeaturedImage)
      return
    }

    const file = files[0].file

    if (file) {
      const base64 = await imageApi.getImageData(file)
      editor.update('vipFeaturedImage', base64)
      setVipFeaturedImage(base64)
    }
  }

  const loadModelGallery = async () => {
    const res = await getModelGallery(srcId(), 1, 1000)
    if (res?.result?.images) {
      setModelGalleryImages(res?.result?.images)
    }
  }

  const uploadModelGalleryImages = async (files: UploadFile[]) => {
    setModelGalleryImagesRemaining(files.length)
    for (let x = 0; x < files.length; x++) {
      const f = files[x]
      const base64 = await imageApi.getImageData(f.file)
      if (base64 && props.editId) {
        const filename = await createModelGalleryImage(base64, srcId())
        await loadModelGallery()
      }
      setModelGalleryImagesRemaining(files.length - (x + 1))
    }
    toastStore.success(`Model gallery images have been added`)
  }

  const onSubmit = async (ev: Event) => {
    const payload = editor.payload(true)

    if (props.temp && props.chat) {
      if (editor.state.avatar) {
        const data = await imageApi.getImageData(editor.state.avatar)
        payload.avatar = data
      }
      chatStore.upsertTempCharacter(
        props.chat._id,
        { ...payload, _id: props.editId },
        (result: any) => {
          props.onSuccess?.(result)
          if (paneOrPopup() === 'popup') props.close?.()
        }
      )
    } else if (!forceNew() && props.editId) {
      characterStore.editFullCharacter(props.editId, payload, () => {
        if (isPage) {
          nav(`/character/${props.editId}/chats`)
        } else if (paneOrPopup() === 'popup') {
          props.close?.()
        }
      })
    } else {
      characterStore.createCharacter(payload, (result: any) => {
        editor.update('editId', result._id)
        setForceNew(false)
        if (isPage) nav(`/character/${result._id}/chats`)
      })
    }
  }

  const footer = (
    <>
      <Button onClick={cancel} schema="secondary">
        <X />
        {props.close ? 'Close' : 'Cancel'}
      </Button>
      <Button onClick={onSubmit} disabled={state.creating}>
        <Save />
        {props.editId && !forceNew() ? 'Update' : 'Create'}
      </Button>
    </>
  )

  const showWarning = createMemo(
    () => !!props.chat?.overrides && props.chat.characterId === props.editId
  )

  const tabs = useTabs(
    ['Persona', 'Voice', 'Model Info', 'Gallery', 'Images', 'Advanced'],
    +(search.char_tab || '0')
  )

  return (
    <Page
      classList={{
        'p-0': !isPage,
      }}
    >
      <Show when={!props.noTitle && (isPage || paneOrPopup() === 'pane')}>
        <PageHeader
          title={`${
            forceNew() ? 'Create' : props.editId ? 'Edit' : props.duplicateId ? 'Copy' : 'Create'
          } a Character`}
          subtitle={
            <>
              <div class="whitespace-normal">
                <em>
                  {totalTokens()} tokens, {totalPermanentTokens()} permanent
                </em>
              </div>
              <Button size="pill" class="w-fit" onClick={() => startTour('char', true)}>
                AI Character Generation Guide
              </Button>
            </>
          }
        />
      </Show>
      <form class="relative text-base">
        <div class="flex flex-col gap-4">
          <Show when={!isPage}>
            <div> {props.children} </div>
          </Show>

          <Show when={showWarning()}>
            <SolidCard bg="orange-600">
              <b>Warning!</b> Your chat currently overrides your character definitions. These
              changes won't affect your current chat until you disable them in the "Edit Chat" menu.
            </SolidCard>
          </Show>

          <div class={`flex grow flex-col justify-between gap-2`}>
            <Show when={!isPage && paneOrPopup() === 'popup'}>
              <div>
                <em>
                  ({totalTokens()} tokens, {totalPermanentTokens()} permanent)
                </em>
              </div>
            </Show>

            <Show when={props.temp}>
              <TitleCard type="premium">
                You are {props.editId ? 'editing' : 'creating'} a temporary character. A temporary
                character exist within your current chat only.
              </TitleCard>
            </Show>

            <div class="flex justify-end gap-2 text-[1em]">
              <Button onClick={() => setOpenPreset(true)} class="tour-preset">
                <SlidersVertical size={24} /> Preset
              </Button>
              <Button onClick={() => setImport(true)}>
                <Import /> Import
              </Button>

              <Button onClick={() => setConverted(editor.convert())}>
                <Download /> Export
              </Button>

              <Show when={state.edit}>
                <Button
                  onClick={() => {
                    setForceNew(true)
                    editor.clear()
                  }}
                >
                  <Plus />
                  New
                </Button>
              </Show>

              <Show when={!state.edit}>
                <Button
                  schema="warning"
                  onClick={() => {
                    settingStore.openConfirm({
                      message: 'Are you sure you wish to clear the editor?',
                      onConfirm: clearEditor,
                    })
                  }}
                >
                  <X />
                </Button>
              </Show>
            </div>

            <Tabs
              select={(id) => {
                tabs.select(id)
                setSearch({ char_tab: id })
              }}
              selected={tabs.selected}
              tabs={tabs.tabs}
            />

            <div class="flex flex-col gap-2" classList={{ hidden: tabs.current() !== 'Persona' }}>
              <Card class="tour-prefields">
                <ButtonInput
                  fieldName="name"
                  required
                  label="Character Name"
                  placeholder=""
                  value={editor.state.name}
                  parentClass="pb-2"
                  onChange={(ev) => editor.update('name', ev.currentTarget.value)}
                >
                  <Button
                    size="sm"
                    schema="input"
                    onClick={() => random('first', {}).then((name) => editor.update('name', name))}
                  >
                    <Dices size={12} />
                  </Button>
                </ButtonInput>

                <FormLabel
                  label="Description / Creator's notes"
                  helperText={
                    <div class="flex flex-col">
                      <span>
                        A description, label, or notes for your character. This is will not
                        influence your character in any way.
                      </span>
                    </div>
                  }
                />

                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    isMultiline
                    fieldName="description"
                    parentClass="w-full"
                    value={editor.state.description}
                    onChange={(ev) => editor.update('description', ev.currentTarget.value)}
                  />
                </div>
              </Card>

              <Card>
                <TagInput
                  availableTags={tagState.tags.map((t) => t.tag)}
                  value={editor.state.tags}
                  fieldName="_tags"
                  label="Tags"
                  helperText="Used to help you organize and filter your characters."
                  onSelect={(tags) => editor.update({ tags })}
                />
              </Card>

              <AvatarField
                user={user}
                editor={editor}
                updateFile={updateFile}
                showBuilder={setShowBuilder}
                image={image}
                setImageUrl={setImageUrl}
                forceNew={forceNew}
                spriteRef={spriteRef}
              />

              <Card>
                <TextInput
                  fieldName="scenario"
                  label={
                    <>
                      <Regenerate
                        field={'scenario'}
                        editor={editor}
                        allowed={editor.canGuidance}
                        class="tour-gen-field"
                      />
                      Scenario{' '}
                    </>
                  }
                  helperText="The current circumstances and context of the conversation and the characters."
                  placeholder="E.g. {{char}} is in their office working. {{user}} opens the door and walks in."
                  value={editor.state.scenario}
                  onChange={(ev) => editor.update('scenario', ev.currentTarget.value)}
                  isMultiline
                  tokenCount={(v) => setTokens((prev) => ({ ...prev, scenario: v }))}
                />
              </Card>

              <Card class="flex flex-col gap-3">
                <div>
                  <FormLabel
                    label={
                      <div class="flex items-center gap-1">
                        <Show when={editor.state.personaKind === 'text'}>
                          <Regenerate
                            field={'persona'}
                            editor={editor}
                            allowed={editor.canGuidance}
                          />
                        </Show>
                        Personality
                      </div>
                    }
                    helperText={
                      <>
                        <p>If you do not know what this mean, you can leave this as-is.</p>
                        <p class="font-bold">
                          WARNING: "Plain Text" and "Non-Plain Text" schemas are not compatible.
                          Changing between them will cause data loss.
                        </p>
                        <p>Format to use for the character's format</p>
                      </>
                    }
                  />
                  <Select
                    fieldName="kind"
                    class="tour-persona"
                    items={personaFormats()}
                    value={editor.state.personaKind}
                    onChange={(ev) => editor.update('personaKind', ev.value as any)}
                  />
                </div>

                <PersonaAttributes
                  schema={editor.state.personaKind}
                  tokenCount={(v) => setTokens((prev) => ({ ...prev, persona: v }))}
                  generate={editor.generateField}
                  state={editor.state.personaAttrs}
                  setter={(next) => editor.update('personaAttrs', next)}
                />
              </Card>
              <Card class="flex flex-col gap-3">
                <TextInput
                  isMultiline
                  fieldName="greeting"
                  label={
                    <>
                      <Regenerate field={'greeting'} editor={editor} allowed={editor.canGuidance} />
                      Greeting{' '}
                    </>
                  }
                  helperText="The first message from your character. It is recommended to provide a lengthy first message to encourage the character to give longer responses."
                  placeholder={
                    "E.g. *I smile as you walk into the room* Hello, {{user}}! I can't believe it's lunch time already! Where are we going?"
                  }
                  value={editor.state.greeting}
                  onChange={(ev) => editor.update('greeting', ev.currentTarget.value)}
                  class="h-60"
                  tokenCount={(v) => setTokens((prev) => ({ ...prev, greeting: v }))}
                />
                <AlternateGreetingsInput
                  greetings={editor.state.alternateGreetings}
                  setGreetings={(next) => editor.update({ alternateGreetings: next })}
                />
              </Card>
              <Card>
                <TextInput
                  isMultiline
                  fieldName="sampleChat"
                  label={
                    <>
                      <Regenerate
                        field={'sampleChat'}
                        editor={editor}
                        allowed={editor.canGuidance}
                      />
                      Sample Conversation{' '}
                    </>
                  }
                  helperText={
                    <span>
                      Example chat between you and the character. This section is very important for
                      teaching your character should speak.
                    </span>
                  }
                  placeholder="{{char}}: *smiles and waves back* Hello! I'm so happy you're here!"
                  value={editor.state.sampleChat}
                  onChange={(ev) => editor.update('sampleChat', ev.currentTarget.value)}
                  tokenCount={(v) => setTokens((prev) => ({ ...prev, sample: v }))}
                />
              </Card>
            </div>

            <div class="flex flex-col gap-2" classList={{ hidden: tabs.current() !== 'Voice' }}>
              <Card class="flex flex-col gap-3">
                <h4 class="text-md font-bold">Voice</h4>
                <Toggle
                  fieldName="voiceDisabled"
                  value={editor.state.voiceDisabled}
                  label="Disable Character's Voice"
                  helperText="Toggle on to disable this character from automatically speaking"
                  onChange={(ev) => editor.update('voiceDisabled', ev)}
                />

                <VoicePicker
                  value={editor.state.voice}
                  culture={editor.state.culture}
                  onChange={(voice) => editor.update('voice', voice)}
                />

                <Select
                  fieldName="culture"
                  label="Language"
                  helperText={`The language this character speaks and understands.${
                    editor.state.culture.startsWith('en') ?? true
                      ? ''
                      : ' NOTE: You need to also translate the preset gaslight to use a non-english language.'
                  }`}
                  value={editor.state.culture}
                  items={CultureCodes}
                  onChange={(option) => editor.update('culture', option.value)}
                />
              </Card>
            </div>

            <div
              class={`flex flex-col gap-2`}
              classList={{ hidden: tabs.current() !== 'Advanced' }}
            >
              <AdvancedOptions editor={editor} />
            </div>

            <div
              class={`flex items-center gap-2 text-center`}
              classList={{ hidden: tabs.current() !== 'Images' }}
            >
              <AIFansImageSettings cfg={editor.state.imageSettings} editor={editor} />
            </div>

            <div
              class={`flex flex-col gap-2`}
              classList={{ hidden: tabs.current() !== 'Model Info' }}
            >
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Real Name"
                  helperText={
                    <div class="flex flex-col">
                      <span>Model's real name shown on the home page and model page</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="realName"
                    parentClass="w-full"
                    value={editor.state.realName}
                    onChange={(ev) => editor.update('realName', ev.currentTarget.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="URL Slug"
                  helperText={
                    <div class="flex flex-col">
                      <span>
                        The URL-friendly version of the name (e.g. 'jane-doe'). Used in the model's
                        page URL.
                      </span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="slug"
                    parentClass="w-full"
                    value={editor.state.slug}
                    placeholder="e.g. jane-doe"
                    onChange={(ev) => editor.update('slug', ev.currentTarget.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Category Slug"
                  helperText={
                    <div class="flex flex-col">
                      <span>
                        The URL-friendly version of the category (e.g. 'popular', 'trending'). Used
                        in category page URLs.
                      </span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="categorySlug"
                    parentClass="w-full"
                    value={editor.state.categorySlug}
                    placeholder="e.g. popular"
                    onChange={(ev) => editor.update('categorySlug', ev.currentTarget.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <TextInput
                  fieldName="metaDescription"
                  value={editor.state.metaDescription}
                  label="Meta Description"
                  helperText="Description used for SEO meta tags"
                  placeholder="Enter a description for search engines"
                  onChange={(ev) => editor.update('metaDescription', ev.currentTarget.value)}
                />
              </Card>
              <Card class="flex w-full flex-col">
                <TextInput
                  fieldName="controlNetImage"
                  class="hidden"
                  value={controlNetImage() || editor.state.controlNetImage}
                  label="ControlNet Image"
                  helperText="(Optional) Image used in ControlNet for face consistency"
                  placeholder={``}
                />

                <Show when={controlNetImage()}>
                  <div class="relative mx-5 flex h-12 w-12 flex-col items-center justify-center overflow-hidden rounded-md bg-cosplay-gray-800/90 px-4 py-4 md:shrink-0 md:grow-0 md:px-8 md:py-8">
                    <img class="absolute h-full w-full object-cover" src={controlNetImage()} />
                  </div>
                </Show>

                <FileInput
                  class="w-full"
                  fieldName="controlNetImage_file"
                  label="ControlNet Image"
                  accept="image/png,image/jpeg,image/apng"
                  onUpdate={updateControlNetImage}
                />
              </Card>
              <Card class="flex w-full flex-col">
                <TextInput
                  fieldName="vipFeaturedImage"
                  class="hidden"
                  value={vipFeaturedImage() || editor.state.vipFeaturedImage}
                  label="VIP Featured Image"
                  helperText="(Optional) Featured image shown on VIP sign up modal"
                  placeholder={``}
                />

                <Show when={vipFeaturedImage()}>
                  <div class="relative mx-5 flex h-12 w-12 flex-col items-center justify-center overflow-hidden rounded-md bg-cosplay-gray-800/90 px-4 py-4 md:shrink-0 md:grow-0 md:px-8 md:py-8">
                    <img class="absolute h-full w-full object-cover" src={vipFeaturedImage()} />
                  </div>
                </Show>

                <FileInput
                  class="w-full"
                  fieldName="vipFeaturedImage_file"
                  label="VIP Featured Image"
                  accept="image/png,image/jpeg,image/apng"
                  onUpdate={updateVipFeaturedImage}
                />
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Category"
                  helperText={
                    <div class="flex flex-col">
                      <span>This determines where the model is shown on the home page</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <Select
                    fieldName="category"
                    items={categoryOptions}
                    value={editor.state.category}
                    onChange={(ev) => editor.update('category', ev.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Genre"
                  helperText={
                    <div class="flex flex-col">
                      <span>Content genre used by the Characters &amp; Following filter pills</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <Select
                    fieldName="genre"
                    items={genreOptions}
                    value={editor.state.genre}
                    onChange={(ev) => editor.update('genre', ev.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Personality"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Outgoing, Seductive</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="personality"
                    parentClass="w-full"
                    value={editor.state.personality}
                    onChange={(ev) => editor.update('personality', ev.currentTarget.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Occupation"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Influencer</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="occupation"
                    parentClass="w-full"
                    value={editor.state.occupation}
                    onChange={(ev) => editor.update('occupation', ev.currentTarget.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Hobbies"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Photography, Fitness</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="hobbies"
                    parentClass="w-full"
                    value={editor.state.hobbies}
                    onChange={(ev) => editor.update('hobbies', ev.currentTarget.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Relationship"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Single</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="relationship"
                    parentClass="w-full"
                    value={editor.state.relationship}
                    onChange={(ev) => editor.update('relationship', ev.currentTarget.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Body"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Fit</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="bodyType"
                    parentClass="w-full"
                    value={editor.state.bodyType}
                    onChange={(ev) => editor.update('bodyType', ev.currentTarget.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Age"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: 24</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="age"
                    parentClass="w-full"
                    value={editor.state.age}
                    onChange={(ev) => editor.update('age', ev.currentTarget.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Butt"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Medium</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="butt"
                    parentClass="w-full"
                    value={editor.state.butt}
                    onChange={(ev) => editor.update('butt', ev.currentTarget.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Hair"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Long, Black</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="hair"
                    parentClass="w-full"
                    value={editor.state.hair}
                    onChange={(ev) => editor.update('hair', ev.currentTarget.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Breasts"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: Huge, DD</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="breasts"
                    parentClass="w-full"
                    value={editor.state.breasts}
                    onChange={(ev) => editor.update('breasts', ev.currentTarget.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Ethnicity"
                  helperText={
                    <div class="flex flex-col">
                      <span>Example: American</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="ethnicity"
                    parentClass="w-full"
                    value={editor.state.ethnicity}
                    onChange={(ev) => editor.update('ethnicity', ev.currentTarget.value)}
                  />
                </div>
              </Card>
              <Card class="flex w-full flex-col">
                <FormLabel
                  label="Country"
                  helperText={
                    <div class="flex flex-col">
                      <span>Two letter country code. Examples: US, AR, ES, MX</span>
                    </div>
                  }
                />
                <div class="flex w-full flex-col gap-2">
                  <TextInput
                    fieldName="country"
                    parentClass="w-full"
                    value={editor.state.country}
                    onChange={(ev) => editor.update('country', ev.currentTarget.value)}
                  />
                </div>
              </Card>
            </div>

            <div class={`flex flex-col gap-2`} classList={{ hidden: tabs.current() !== 'Gallery' }}>
              <Show when={modelGalleryImagesRemaining() > 0}>
                <div class="mt-2 text-xs">
                  Uploading {modelGalleryImagesRemaining()} images to the model's gallery...
                </div>
              </Show>
              <input
                class="mt-2"
                type="file"
                multiple
                use:fileUploader={{
                  userCallback: uploadModelGalleryImages,
                  setFiles,
                }}
                onClick={(ev) => {
                  ev.stopPropagation()
                }}
              />
              <div class="my-3 grid grid-cols-6 gap-3">
                {modelGalleryImages().map((image) => {
                  return (
                    <div class="group relative ">
                      <div
                        class="absolute -right-1 -top-1 hidden h-4 w-4 cursor-pointer rounded-full bg-white group-hover:block"
                        onClick={async () => {
                          if (confirm('Are you sure you want to remove this image?')) {
                            await removeModelGalleryImage(image._id)
                            toastStore.success('Image removed from model gallery')
                            await loadModelGallery()
                          }
                        }}
                      >
                        <X aria-hidden="true" class="absolute h-4 w-4 text-black" />
                      </div>
                      <img class="" src={getAssetUrl(image.url)} />
                    </div>
                  )
                })}
              </div>
            </div>

            <Show when={!props.close}>
              <div class="flex w-full justify-end gap-2">{footer}</div>
            </Show>
          </div>
        </div>
      </form>
      <Show when={showBuilder()}>
        <SpriteModal
          body={editor.state.sprite}
          onChange={(body) => {
            editor.update('sprite', body)
            setShowBuilder(false)
          }}
          show={showBuilder()}
          close={() => setShowBuilder(false)}
        />
      </Show>

      <Show when={converted()}>
        <DownloadModal
          show
          close={() => setConverted(undefined)}
          char={converted()!}
          charId={converted()!._id}
        />
      </Show>
      <ImportCharacterModal
        show={showImport()}
        close={() => setImport(false)}
        onSave={async (char, imgs) => {
          await editor.load(char[0])
          editor.receiveAvatar(imgs[0]!)
          setImage(imgs[0] as any)
          setImport(false)
        }}
        single
      />

      <AvatarModal url={imgUrl()} close={() => setImageUrl('')} />

      <Show when={openPreset()}>
        <RootModal
          title="Update Preset"
          show
          close={() => setOpenPreset(false)}
          maxWidth="half"
          maxHeight
          footer={presetFooter()}
        >
          <sub>This preset used for character generation</sub>
          <ModeGenSettings
            presetId={user.user?.chargenPreset || user.user?.defaultPreset}
            onPresetChanged={(id) => userStore.updatePartialConfig({ chargenPreset: id })}
            close={() => setOpenPreset(false)}
            hideTabs={['Memory', 'Prompt']}
            footer={setPresetFooter}
          />
        </RootModal>
      </Show>
    </Page>
  )
}
